// react
import { useContext } from 'react';

// router
import { Outlet, useLocation, useSearchParams } from 'react-router-dom';

// context
import { ContextSpendPlan, SpendPlanProvider } from './ProjectV2.context';

// dialogs
import { DialogAddActivity } from '../../dialogs/DialogAddActivity/DialogAddActivity';
import { DialogAddCondition } from '../../dialogs/DialogAddCondition/DialogAddCondition';
import { DialogAddContract } from '../../dialogs/DialogAddContract/DialogAddContract';
import { DialogCreateWorkPlan } from '../../dialogs/DialogCreateWorkPlan/DialogCreateWorkPlan';
import { DialogLinkContract } from '../../dialogs/DialogLinkContract/DialogLinkContract';

/**
 * Spend-plan routing wrapper.
 *
 * Sidebar layout has been lifted to `Project/V2SidebarLayout.tsx` (rendered by
 * `Project.tsx` whenever `(project.version === '2')`) so the same nav is available
 * across all v2 routes — not just spend-plan sub-routes. This component now
 * provides spend-plan context, mounts spend-plan-scoped dialogs, and renders
 * the child route via `<Outlet />`.
 */
const SpendPlanContent = () => {
	const location = useLocation();
	const [searchParams, setSearchParams] = useSearchParams();

	const {
		activities,
		conditions,
		contracts,
		workPlans,
		refetchActivities,
		refetchConditions,
		refetchContracts,
		refetchWorkPlans,
	} = useContext(ContextSpendPlan);

	// dialog params
	const paramDialog = searchParams.get('dialog');
	const paramDialogWorkPlan = searchParams.get('dialogWorkPlan');
	const paramDialogActivity = searchParams.get('dialogActivity');
	const paramDialogContract = searchParams.get('dialogContract');
	const paramDialogCondition = searchParams.get('dialogCondition');
	const paramDialogParentActivity = searchParams.get('dialogParentActivity');
	const paramDialogComponent = searchParams.get('dialogComponent');
	const paramDialogCategory = searchParams.get('dialogCategory');
	const paramContextType = searchParams.get('dialogContextType') as 'workPlan' | 'activity' | null;
	const paramContextId = searchParams.get('dialogContextId');

	// Check if we're on a detail page (to avoid rendering duplicate dialogs)
	const isOnDetailPage = /\/(condition|contract|activity|work-plan|voucher)\/\d+/.test(
		location.pathname
	);

	// selected items for dialogs (edit mode)
	const editWorkPlan = paramDialogWorkPlan
		? workPlans.find((e) => e.id === Number(paramDialogWorkPlan)) || null
		: null;

	const editActivity = paramDialogActivity
		? activities.find((a) => a.id === Number(paramDialogActivity)) || null
		: null;

	const editContract = paramDialogContract
		? contracts.find((c) => c.id === Number(paramDialogContract)) || null
		: null;

	const editCondition = paramDialogCondition
		? conditions.find((c) => c.id === Number(paramDialogCondition)) || null
		: null;

	// derived - preselected context for add condition dialog
	// Supports both dialogWorkPlan param (from work plan page) and dialogContextType/Id params (from Conditions tab)
	const preSelectedConditionContext = paramDialogWorkPlan
		? { workPlanId: Number(paramDialogWorkPlan) }
		: paramContextId
			? {
					...(paramContextType === 'workPlan' && { workPlanId: Number(paramContextId) }),
					...(paramContextType === 'activity' && { activityId: Number(paramContextId) }),
				}
			: undefined;

	// handlers
	const handleCloseDialog = () => {
		const params = new URLSearchParams(searchParams);
		params.delete('dialog');
		params.delete('dialogWorkPlan');
		params.delete('dialogActivity');
		params.delete('dialogContract');
		params.delete('dialogParentActivity');
		params.delete('dialogComponent');
		params.delete('dialogCategory');
		setSearchParams(params);
	};

	const handleDialogSuccess = () => {
		refetchActivities();
		refetchContracts();
		refetchWorkPlans();
		handleCloseDialog();
	};

	return (
		<>
			<Outlet />

			{/* Global Dialogs - Available from any tab */}
			<DialogCreateWorkPlan
				workPlan={paramDialog === 'editWorkPlan' ? editWorkPlan : null}
				isOpen={
					paramDialog === 'createWorkPlan' ||
					(paramDialog === 'editWorkPlan' && editWorkPlan !== null)
				}
				onClose={handleCloseDialog}
				onSuccess={handleDialogSuccess}
			/>
			<DialogAddActivity
				activity={paramDialog === 'editActivity' ? editActivity : null}
				isOpen={
					paramDialog === 'addActivity' ||
					(paramDialog === 'editActivity' && editActivity !== null) ||
					paramDialog === 'addSubActivity'
				}
				onClose={handleCloseDialog}
				onSuccess={handleDialogSuccess}
				parentActivityId={paramDialogParentActivity ? Number(paramDialogParentActivity) : undefined}
				preSelectedCategoryId={paramDialogCategory ? Number(paramDialogCategory) : undefined}
				preSelectedComponentId={paramDialogComponent ? Number(paramDialogComponent) : undefined}
				preSelectedWorkPlanId={paramDialogWorkPlan ? Number(paramDialogWorkPlan) : undefined}
			/>
			<DialogLinkContract
				contract={null}
				isOpen={paramDialog === 'linkContract'}
				onClose={handleCloseDialog}
				onSuccess={handleDialogSuccess}
				preSelectedActivityId={paramDialogActivity ? Number(paramDialogActivity) : undefined}
				preSelectedContractId={
					paramDialogContract && paramDialog === 'linkContract'
						? Number(paramDialogContract)
						: undefined
				}
			/>
			{/* Contract edit uses the unified V1-based form (legal fields + V2
			    spend-plan assignment + custom fields), launched from both the
			    contracts listing row and the contract detail page. */}
			<DialogAddContract
				contract={paramDialog === 'editContract' ? editContract : null}
				isOpen={paramDialog === 'editContract' && editContract !== null}
				onClose={handleCloseDialog}
				onSuccess={handleDialogSuccess}
			/>
			<DialogAddCondition
				condition={paramDialog === 'editCondition' ? editCondition : null}
				isOpen={
					!isOnDetailPage &&
					((paramDialog === 'addCondition' && !paramDialogContract) ||
						(paramDialog === 'editCondition' && editCondition !== null))
				}
				onClose={handleCloseDialog}
				onSuccess={() => {
					refetchConditions();
					handleCloseDialog();
				}}
				preSelectedContext={preSelectedConditionContext}
			/>
		</>
	);
};

export const SpendPlan = () => {
	return (
		<SpendPlanProvider>
			<SpendPlanContent />
		</SpendPlanProvider>
	);
};
